import { memo, useId } from "react";
import type { SelectProps as RadixSelectProps } from "@radix-ui/react-select";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";

export type SelectFieldProps = {
  label?: string;
  errorMessage?: string;
  value?: string;
  placeholder?: string;
  options?: string[];
  onChange?: (value: string) => void;
} & React.ComponentProps<"select"> &
  RadixSelectProps;

const SelectField = ({
  className,
  id,
  label,
  options,
  required,
  errorMessage,
  placeholder,
  onChange = () => {},
  ...props
}: SelectFieldProps) => {
  return (
    <div className="grid grid-cols-1 place-content-start gap-2">
      <div className="grid grid-cols-1 place-content-start gap-2 md:gap-2.5">
        {!!label && (
          <label
            className={cn(
              "text-semantic-text-primary text-base font-normal leading-[130%] md:text-gray-900 md:text-sm md:leading-[20px] md:font-medium",
              {
                "after:content-['*'] after:ml-2 after:text-error": required,
              }
            )}
          >
            {label}
          </label>
        )}
        <Select onValueChange={onChange} {...props}>
          <SelectTrigger
            className={cn(
              "outline-none w-full py-[12.8px] px-3 text-base leading-[130%] bg-white text-semantic-text-primary border border-solid border-neutral-200 rounded-[12px] transition-colors disabled:cursor-not-allowed disabled:opacity-50 focus-visible:border-blue md:py-[21.2px] md:px-7.5 md:rounded-[50px] md:text-sm md:leading-[100%] data-[state=open]:border-blue data-[size=default]:h-auto",
              { "border-error": !!errorMessage },
              className
            )}
          >
            <SelectValue
              placeholder={placeholder}
              className="text-muted-foreground text-base leading-[130%] transition-opacity opacity-100 duration-300 md:text-sm md:leading-[100%]"
            />
          </SelectTrigger>
          <SelectContent>
            {options?.map((option, i) => {
              return <SelectItem key={i} value={option}>{option}</SelectItem>;
            })}
          </SelectContent>
        </Select>
      </div>
      {!!errorMessage && (
        <span className="text-error text-sm">{errorMessage}</span>
      )}
    </div>
  );
};

export default memo(SelectField);
